import { bool, func, node, number, oneOfType, string } from "prop-types";
import { useEffect, useMemo, useState } from "react";

import styled from "styled-components";
import { borderRadius, padding, shadow } from "theme";
import {
  SMALL_POPOVER_WIDTH,
  TINY_POPOVER_WIDTH,
  shadowVariants,
  themeVariants,
} from "theme/constants";

import FnrTypography from "FnrTypography";
import { AnimatePresence, motion } from "framer-motion";

import FnrIconButton from "UI/FnrIconButton";
import FnrMovingDiv from "UI/FnrMovingDiv";
import {
  dangle,
  explode,
  explodeFromPoint,
  inOutZoom,
  straighten,
} from "UI/motionStyles";

const ZeroDiv = styled(motion.div)`
  width: 0px;
  height: 0px;
  z-index: 10;
  position: relative;
`;

const SMotionDiv = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({
    theme: {
      colors: { fnYellowNotice },
    },
  }) => fnYellowNotice};
  width: ${({ $compact }) =>
    $compact ? TINY_POPOVER_WIDTH : SMALL_POPOVER_WIDTH}px;
  ${({ $xOnRight }) =>
    $xOnRight
      ? "flex-direction: row-reverse; justify-content: space-between;"
      : ""}
  ${padding}
  ${borderRadius}
  ${shadow}
`;

const ContainerRelay = ({ inline, children, showOuter, ...rest }) => {
  if (!inline)
    return (
      <AnimatePresence>
        {showOuter && <ZeroDiv {...rest}>{children}</ZeroDiv>}
      </AnimatePresence>
    );
  return (
    <FnrMovingDiv widthMotion heightMotion clip={false}>
      {children}
    </FnrMovingDiv>
  );
};

ContainerRelay.propTypes = { inline: bool, children: node, showOuter: bool };

/**
 *
 * @param {object} param0
 * @param {function} param0.onDismiss - this is fired when the user dismisses the error.
 * @param {boolean} param0.explode - adds an springy explosive feel to the error message.
 * @param {boolean} param0.dangle - adds an addition amount of intensity to the explode prop. no op if explode is false or in inline is true.
 * @param {boolean} param0.inline - makes the box for the error message inline. This will create layout changes.
 * @param {boolean} param0.xOnRight - puts the X on the right and aligns text left.
 * @param {number} param0.translateX - adjust the position of the message on the x axis
 * @param {number} param0.translateY - adjust the position of the message on the y axis
 * @param {boolean} param0.compact - makes it tiny.
 * @param {import("react").ReactNode} param0.children
 */
const FnrDismissibleError = ({
  onDismiss,
  show: showArg,
  errorMessage,
  explode: shouldExplode,
  dangle: shouldDangle,
  inline,
  children,
  xOnRight,
  translateX,
  translateY,
  compact,
  ...rest
}) => {
  const hasError = Boolean(errorMessage);
  const [showLocal, setShowLocal] = useState(Boolean(hasError));
  const [showOuter, setShowOuter] = useState(false);

  useEffect(() => {
    if (showLocal !== showOuter) setShowOuter(showLocal);
  }, [showLocal, showOuter]);

  const dismiss = () => {
    if (typeof onDismiss === "function") onDismiss();
    setShowLocal(false);
  };

  useEffect(() => {
    setShowLocal(hasError);
  }, [hasError, showArg]); // showArg should cause local state to refresh

  const show = useMemo(() => {
    if (typeof showArg === "boolean") return showArg;
    else return showLocal;
  }, [showArg, showLocal]);

  const outerAnimationProps = useMemo(() => {
    const coreAnimation = shouldDangle && shouldExplode ? dangle : straighten;
    return {
      ...coreAnimation,
      animate: { ...coreAnimation.animate, x: translateX, y: translateY },
    };
  }, [shouldDangle, shouldExplode, translateX, translateY]);

  const innerAnimationProps = useMemo(() => {
    if (shouldExplode && shouldDangle) return explodeFromPoint;
    if (shouldExplode) return explode;
    return inOutZoom;
  }, [shouldDangle, shouldExplode]);
  return (
    <ContainerRelay
      data-cy="FnrDismissibleError_Container"
      inline={inline}
      showOuter={showOuter}
      {...outerAnimationProps}
      {...rest}
    >
      <AnimatePresence>
        {show && (
          <SMotionDiv
            $variant={themeVariants.container}
            $shadowVariant={shadowVariants.insetDark}
            $contentEmphasized
            $xOnRight={xOnRight}
            $compact={compact}
            {...innerAnimationProps}
          >
            <FnrIconButton
              aria-label="dismiss-button"
              icon="fa-times fas"
              variant="warning"
              onClick={dismiss}
              size={compact ? "sm" : "md"}
            />
            {children}
            <FnrTypography
              type="copy"
              color="textWarning"
              fontSize={compact ? "xs" : "sm"}
            >
              {errorMessage}
            </FnrTypography>
          </SMotionDiv>
        )}
      </AnimatePresence>
    </ContainerRelay>
  );
};

FnrDismissibleError.propTypes = {
  onDismiss: func,
  errorMessage: oneOfType([string, bool, func]),
  explode: bool,
  dangle: bool,
  inline: bool,
  children: node,
  xOnRight: bool,
  translateX: number,
  translateY: number,
  compact: bool,
  show: bool,
};

export default FnrDismissibleError;
